/**
 * @module plugins/musicfavourite/model/map
 *
 * Data structure for an ordered map of a user's personalised items.
 * Map is ordered by Items postedTime in ascending order. Data structure
 * wraps JSON output from SNeS.
 *
 * Maps can also be bounded, with a limit as specified by the max property.
 */
define(
    'plugins/musicfavourite/model/map',['../util', './item', 'exports'],
    function(util, Item, /* exports */ Map) {


        Map = function(/* string | object | Array */ data) {
            this.entry = []; // Sorted array based on Item.postedTime from oldest to newest
            this.types = {};
            this.length = 0;
            this.max = undefined;

            // Unserialise data to new map and ignore errors
            data && this.addAll(data, true, true);
        };



        Map.prototype = {

            /**
             * Adds an Item to the map. Will update the postedTime of an
             * object if it already exists. Will check against the Map's max
             * bound if set.
             *
             * @param {Item|object} item the item to add.
             * @param {boolean=} replaceExistingFlag replace existing when found (default true)
             * @returns {Item|false}
             * @throws Map.LimitReachedError when the map has reached its maximum bound as
             *     specified by this.max
             */
            add: function(/* Item | object */ item, replaceExistingFlag) {

                // Find existing item, and if its older then replace, otherwise return false (no add)
                var existing = this.find(item);
                if (existing) {
                    return (replaceExistingFlag !== false && item.postedTime > existing.postedTime) ? this.replace(existing, item) : false;
                }


                if (!this.canAdd(item)) {
                    throw new Map.LimitReachedError(this);
                }


                if (!(item instanceof Item)) {
                    item = new Item(item);
                }


                return this._insertEntry(item); // performs the add
            },



            /**
             * @param {boolean=} replaceExistingFlag replace existing when found (default true)
             * @return Array of items fav'ed
             */
            addAll: function(/* string | object | Array */ data, replaceExistingFlag, /* boolean */ ignoreErrorsFlag) {

                if (typeof(data) == 'string') {
                    data = util.json.parse(data);
                }

                if (data instanceof Array) {
                    var output = [], item;
                    for (item, i = 0; (item = data[i]); i++) {

                        if (ignoreErrorsFlag) {
                            try {
                                item = this.add(item, replaceExistingFlag);

                                item && output.push(item);

                            } catch (err) {
                                var cs = window.console;

                                if (cs && typeof(cs.warn) === 'function') {
                                    cs.warn('Personalisation error: ' + err);
                                }
                            }

                        } else {
                            item = this.add(item, replaceExistingFlag);

                            item && output.push(item);
                        }

                    }

                    return output;

                } else if (typeof(data) == 'object') {
                    if (data.entry) {
                        return this.addAll(data.entry, replaceExistingFlag, ignoreErrorsFlag);

                    } else { // try direct add
                        return this.add(data, replaceExistingFlag);
                    }

                }

                return false;
            },


            /**
             * Determine if the given item can be added to the Map. Usually
             * checks against max bound of map.
             */
            canAdd: function(/* Item */ item) {
                return !this.isFull();
            },


            contains: function() {
                return !!this.find.apply(this, arguments);
            },


            // Note: native Array.forEach may not be that fast, so not wrapping it
            //       See: http://jsperf.com/for-vs-foreach/32
            each: function(callback, context, reverse_flag) {
                if (typeof(callback) == 'function') {

                    var returnValue, item,

                    i = reverse_flag ? this.length - 1 : 0, // end or start
                    modifier = reverse_flag ? -1 : 1; // i-- or i++

                    context = context || this;

                    for (; (item = this.entry[i]); i += modifier) {
                        returnValue = callback.call(context, item, i, this.entry);
                        if (returnValue !== undefined) {
                            return returnValue;
                        }
                    }
                }

                // return undefined;
            },


            eachInReverse: function(callback, context) {
                return this.each(callback, context, true);
            },

            equals: function(object) {

                if (object === this) {
                    return true;
                }

                if (typeof(object) == 'string') {
                    return this.equals(util.json.parse(object));
                }

                var length = object.length || object.totalResults;

                if (!(object.entry instanceof Array) || length !== undefined && length != this.length) {
                    return false;
                }

                return this.each(function(item, index) {
                    if (!item.equals(object.entry[index])) {
                        return false;
                    }
                }) === undefined;

            },


 
            find: function(/* object | string */ id, /* string | undefined */ type) {
                if (typeof(id) == 'object' && id.id && id.type) {
                    type = id.type;
                    id = id.id;
                }
                
                type = this.types[type]; // overloading type var with type map object

                return type && type[id];
            },



            /**
             * Retrieves an array of Favourites which pass the test implemented by the provided function.
             *
             * @param  {Function} callback Callback function to test elements to filter. callback is invoked with three arguments:
             *                             - the value of the element
             *                             - the index of the element
             *                             - the Array object being traversed
             * @param {object} thisObject  Object to use as this when executing callback.
             * @return {Array}             New Array of filtered favourites.
             */
            filter: function(callback, thisObject) {
                var favs = this.entry,
                copy = favs.slice(0);

                thisObject = thisObject || this;

                for (var i = favs.length-1; i >= 0; i--) {
                    if (!callback.call(thisObject, favs[i], i, favs)) {
                        copy.splice(i,1);
                    }
                }
                return copy;
            },


            getIndex: function(idx) {
                return this.entry[idx];
            },


            getItemsWithType: function(type) {
                var typeVal = this.types[type];
                if (!typeVal) {
                    typeVal = this.types[type] = {};
                }
                return typeVal;
            },

            isEmpty: function() {
                return !this.length;
            },

            isFull: function() {
                return !isNaN(this.max) && this.length >= this.max;
            },

            remove: function(item) {
                if (!(item instanceof Item)) {
                    item = this.find(item);

                    if (!item) { return false; }
                }

                var idx = this._indexOf(item);
                if (idx === -1) {
                    return false;
                }

                return this._removeIndex(idx, item);
            },


            /**
             * Searches and removes oldItem if found, then adds newItem to Map.
             *
             * @return {Item} the newly added item which replaced oldItem, or false if
             *     oldItem couldn't be removed.
             */
            replace: function(oldItem, newItem) {
                return this.remove(oldItem) && this.add(newItem);
            },






            toJSON: function() {
                return { entry: this.entry };
            },

            toJSONString: function() {
                return util.json.stringify(this);
            },


            toString: function() {
                return 'Map';
            },


            /**
             * Determines the array index of an Item in the entry array.
             */
            _indexOf: function(item) {

                // Doesn't use native function as its slowwww
                for (var existing, i = 0; (existing = this.entry[i]); i++) {
                    if (existing === item || existing.equals(item)) {
                        return i;
                    }
                }

                return -1;

            },

            /**
             * Inserts an item into the entry array based on its posted time, retaining
             * Ascending order of array.
             */
            _insertEntry: function(/* Item */ newItem) {

                this.getItemsWithType(newItem.type)[newItem.id] = newItem; // set this.types[type][id]

                for (var i = this.length; i--;) { // reverse iterator
                    if (newItem.postedTime >= this.entry[i].postedTime) {
                        this.entry.splice(i + 1, 0, newItem);
                        this.length++;
                        return newItem;
                    }
                }

                this.entry.unshift(newItem); // older than everything, so put at front
                this.length++;

                return newItem;
            },


            /**
             * Removes an item by index from the entry array.
             */
            _removeIndex: function(idx, /* optional Item */ item) {

                item = item || this.entry[idx];

                if (item) {
                    this.entry.splice(idx, 1);
                    delete this.types[item.type][item.id];

                    this.length--;
                }

                return item;
            }
        };

        // Wrapper for refactored get method. Renamed to find.
        Map.prototype.get = Map.prototype.find;


        Map.LimitReachedError = function(map) {

            this.name = 'LimitReached';
            this.eventDescriptor = 'limited';
            this.map = map;
            this.max = map.max;
            this.message = 'Maximium number of items reached';

        };
        Map.LimitReachedError.prototype = new Error();

        return Map;
    }
);

